import moment from "moment";
import {
    Box,
    Paper,
    TextField,
    Button,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Divider
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useNavigate, useLocation } from "react-router-dom";

export default function PricingSearch(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const { filters, setFilters, onSearch, setPage, catalogs, locations = {}, ruleEnums } = props;

    const setSearchFilter = (key, value) => {
        if (key === "startDate" && filters.endDate && value && moment(value).isAfter(moment(filters.endDate))) {
            return;
        }

        if (key === "endDate" && filters.startDate && value && moment(value).isBefore(moment(filters.startDate))) {
            return;
        }
        setFilters((prev) => ({ ...prev, [key]: value }));

        if (key === "catalogId") {
            navigate(location.pathname, {
                replace: true,
                state: { ...(location.state || {}), selectedCatalogId: value },
            });
        }
    };

    const handleSearch = () => {
        setPage(0);
        onSearch(filters, true);
    };

    const handleClear = () => {
        const initial = catalogs[0]?.catalogId ?? "";
        const cleared = { catalogId: initial, startDate: null, endDate: null };
        setFilters(cleared);
        setPage(0);
        onSearch(cleared, true);
    };

    return (

        <Paper
            elevation={3}
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 1000,
                p: 3,
                backgroundColor: "#fff",
                borderBottom: "1px solid #eee"
            }}
        >

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                }}
            >
                <Typography variant="h5">
                    Pricing Rules
                </Typography>

                <Button
                    variant="contained"
                    onClick={() =>
                        navigate("/pricing-rules/create", {
                            state: { selectedCatalogId: filters.catalogId }
                        })
                    }
                >
                    Create Rule
                </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Filters */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container spacing={2} alignItems="stretch">
                    <Grid item size={{ xs: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="location-label">Catalog</InputLabel>
                            <Select
                                labelId="search-type-label"
                                value={filters?.catalogId ?? ""}
                                label="Search By Catalog"
                                onChange={(e) =>
                                    setSearchFilter("catalogId", e.target.value)
                                }
                            >
                                {catalogs.map((catalog) => (
                                    <MenuItem
                                        key={catalog.catalogId}
                                        value={catalog.catalogId}
                                    >
                                        {catalog.catalogName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 2 }}>
                        <DatePicker
                            label="From Date"
                            value={
                                filters?.startDate
                                    ? moment(filters.startDate, "YYYY-MM-DD")
                                    : null
                            }
                            onChange={(newValue) =>
                                setSearchFilter(
                                    "startDate",
                                    newValue ? newValue.format("YYYY-MM-DD") : null
                                )
                            }
                            slotProps={{
                                textField: { fullWidth: true, size: "small" }
                            }} />
                    </Grid>

                    <Grid item size={{ xs: 6, md: 2 }}>
                        <DatePicker
                            label="To Date"
                            value={
                                filters?.endDate
                                    ? moment(filters.endDate, "YYYY-MM-DD")
                                    : null
                            }
                            onChange={(newValue) =>
                                setSearchFilter(
                                    "endDate",
                                    newValue ? newValue.format("YYYY-MM-DD") : null
                                )
                            }
                            slotProps={{
                                textField: { fullWidth: true, size: "small" }
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search by ID or Title"
                            value={filters?.query ?? ""}
                            onChange={(e) => setSearchFilter("query", e.target.value)}
                        />
                    </Grid>

                    <Grid item size={{ xs: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <Autocomplete
                                multiple={true}
                                options={locations?.stores ?? []}
                                value={filters?.storeIds ?? []}
                                onChange={(_, value) => setSearchFilter("storeIds", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Select Store"
                                        placeholder="Select…"
                                    />
                                )}
                                getOptionLabel={(option) => option.storeName || option.name || option}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <Autocomplete
                                multiple
                                options={locations?.geoTags ?? []}
                                value={filters?.geoTagIds ?? []}
                                onChange={(_, value) => setSearchFilter("geoTagIds", value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Select Geotag"
                                        placeholder="Select…"
                                    />

                                )}
                                getOptionLabel={(option) => option}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="location-label">Rule State</InputLabel>
                            <Select
                                labelId="search-type-label"
                                value={filters?.ruleState ?? ""}
                                label="Search By State"
                                onChange={(e) =>
                                    setSearchFilter("ruleState", e.target.value)
                                }
                            >
                                {Object.keys(ruleEnums?.RuleState ?? {}).map((key) => (
                                    <MenuItem
                                        key={key}
                                        value={key}
                                    >
                                        {key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="location-label">Rule Type</InputLabel>
                            <Select
                                labelId="search-type-label"
                                value={filters?.ruleType ?? ""}
                                label="Search By Type"
                                onChange={(e) =>
                                    setSearchFilter("ruleType", e.target.value)
                                }
                            >
                                {Object.keys(ruleEnums?.RuleType ?? {}).map((key) => (
                                    <MenuItem
                                        key={key}
                                        value={key}
                                    >
                                        {key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                justifyContent: "flex-start"
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                            >
                                Search
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleClear}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Paper >

    );
}